import React, { useMemo, useState, useEffect } from "react";
import "./InfoBlock.css";

const InfoBlock = ({
  buyOrderBook,
  sellOrderBook,
  exitBuyOrderBook,
  exitSellOrderBook,
  // Новые props для проверки контрактов
  buyExchange,
  buyType,
  sellExchange,
  sellType,
  symbol,
}) => {
  const [exitTargetSpread, setExitTargetSpread] = useState(0); // по умолчанию 0%

  // === Инпут: объём в монетах ===
  const [coinAmount, setCoinAmount] = useState(null);     // число для расчётов
  const [coinAmountStr, setCoinAmountStr] = useState(""); // строка для инпута
  const [autoVolumeInitialized, setAutoVolumeInitialized] = useState(false);

  const roundN = (v, n = 4) => Math.round(v * 10 ** n) / 10 ** n;

  // Покупка по asks на volumeCoins
  function avgBuyFromAsks(asks, volumeCoins) {
    if (!Array.isArray(asks) || asks.length === 0 || !Number.isFinite(volumeCoins) || volumeCoins <= 0) {
      return { avgPrice: 0, total: 0, filled: 0, enoughDepth: false };
    }
    let remain = volumeCoins;
    let cost = 0;
    let filled = 0;
    for (const [pStr, qStr] of asks) {
      const p = parseFloat(pStr), q = parseFloat(qStr);
      if (!(p > 0 && q > 0)) continue;
      const take = Math.min(remain, q);
      cost += take * p;
      filled += take;
      remain -= take;
      if (remain <= 0) break;
    }
    const avg = filled > 0 ? cost / filled : 0;
    return { avgPrice: avg, total: cost, filled, enoughDepth: filled >= volumeCoins - 1e-12 };
  }

  // Продажа в bids на volumeCoins
  function avgSellToBids(bids, volumeCoins) {
    if (!Array.isArray(bids) || bids.length === 0 || !Number.isFinite(volumeCoins) || volumeCoins <= 0) {
      return { avgPrice: 0, total: 0, filled: 0, enoughDepth: false };
    }
    let remain = volumeCoins;
    let proceeds = 0;
    let filled = 0;
    for (const [pStr, qStr] of bids) {
      const p = parseFloat(pStr), q = parseFloat(qStr);
      if (!(p > 0 && q > 0)) continue;
      const take = Math.min(remain, q);
      proceeds += take * p;
      filled += take;
      remain -= take;
      if (remain <= 0) break;
    }
    const avg = filled > 0 ? proceeds / filled : 0;
    return { avgPrice: avg, total: proceeds, filled, enoughDepth: filled >= volumeCoins - 1e-12 };
  }

  // Максимальный объём V, при котором спред входа остаётся > 0
  function maxPositiveEntryVolume(asks, bids) {
    const A = Array.isArray(asks) ? asks : [];
    const B = Array.isArray(bids) ? bids : [];
    if (A.length === 0 || B.length === 0) {
      return { volume: 0, avgBuy: 0, avgSell: 0, spreadPct: 0, cost: 0, proceeds: 0 };
    }

    let i = 0, j = 0;
    let askLeft = parseFloat(A[0][1] || 0);
    let bidLeft = parseFloat(B[0][1] || 0);
    let cumVol = 0, cumCost = 0, cumProceeds = 0;

    let bestVol = 0;
    let bestCost = 0;
    let bestProceeds = 0;

    const next = () => {
      while (i < A.length && !(parseFloat(A[i][0]) > 0 && parseFloat(A[i][1]) > 0)) i++;
      while (j < B.length && !(parseFloat(B[j][0]) > 0 && parseFloat(B[j][1]) > 0)) j++;
      if (i >= A.length || j >= B.length) return false;
      if (!Number.isFinite(askLeft) || askLeft <= 0) askLeft = parseFloat(A[i][1] || 0);
      if (!Number.isFinite(bidLeft) || bidLeft <= 0) bidLeft = parseFloat(B[j][1] || 0);
      if (!(askLeft > 0 && bidLeft > 0)) return false;
      return true;
    };

    while (true) {
      if (!next()) break;

      const askP = parseFloat(A[i][0]);
      const bidP = parseFloat(B[j][0]);
      const chunk = Math.min(askLeft, bidLeft);

      cumVol += chunk;
      cumCost += chunk * askP;
      cumProceeds += chunk * bidP;

      const avgBuy = cumCost / cumVol;
      const avgSell = cumProceeds / cumVol;
      const spreadPct = avgBuy > 0 ? ((avgSell - avgBuy) / avgBuy) * 100 : 0;

      if (spreadPct > 0) {
        // обновляем лучший (наибольший объём с положительным спредом)
        bestVol = cumVol;
        bestCost = cumCost;
        bestProceeds = cumProceeds;
      }

      // сдвигаем уровень(и)
      askLeft -= chunk;
      bidLeft -= chunk;
      if (askLeft <= 1e-12) { i++; askLeft = 0; }
      if (bidLeft <= 1e-12) { j++; bidLeft = 0; }
    }

    if (bestVol <= 0) {
      return { volume: 0, avgBuy: 0, avgSell: 0, spreadPct: 0, cost: 0, proceeds: 0 };
    }

    const avgBuy = bestCost / bestVol;
    const avgSell = bestProceeds / bestVol;
    const spreadPct = avgBuy > 0 ? ((avgSell - avgBuy) / avgBuy) * 100 : 0;

    return {
      volume: bestVol,
      avgBuy,
      avgSell,
      spreadPct,
      cost: bestCost,
      proceeds: bestProceeds,
    };
  }

  // Автоподстановка из $100
  useEffect(() => {
    if (autoVolumeInitialized) return;
    const bestAsk = parseFloat(buyOrderBook?.asks?.[0]?.[0] || 0);
    if (bestAsk > 0) {
      const estCoins = 100 / bestAsk;
      const rounded = roundN(estCoins, 4);
      setCoinAmount(rounded);
      setCoinAmountStr(String(rounded));
      setAutoVolumeInitialized(true);
    }
  }, [buyOrderBook, autoVolumeInitialized]);

  // ====== АНАЛИТИКА ВХОДА ======
  const entryCalc = useMemo(() => {
    const requested = Number(coinAmount);
    if (!(requested > 0)) return null;

    const buyAsks  = buyOrderBook?.asks || [];
    const sellBids = sellOrderBook?.bids || [];

    const leftBuy   = avgBuyFromAsks(buyAsks, requested);
    const rightSell = avgSellToBids(sellBids, requested);

    const matchedVol = Math.min(leftBuy.filled, rightSell.filled);

    let useBuy = leftBuy;
    let useSell = rightSell;
    let depthOk = leftBuy.enoughDepth && rightSell.enoughDepth;

    if (matchedVol > 0 && matchedVol < requested - 1e-12) {
      useBuy  = avgBuyFromAsks(buyAsks, matchedVol);
      useSell = avgSellToBids(sellBids, matchedVol);
    }

    const vol = matchedVol;
    const avgBuyPrice  = vol > 0 ? useBuy.total  / vol : 0;
    const avgSellPrice = vol > 0 ? useSell.total / vol : 0;
    const spreadPct = avgBuyPrice > 0 ? ((avgSellPrice - avgBuyPrice) / avgBuyPrice) * 100 : 0;

    return {
      requestedVolume: requested,
      matchedVolume: vol,
      avgBuyPrice,
      avgSellPrice,
      totalBuyUSDT: useBuy.total,
      totalSellUSDT: useSell.total,
      spreadPct,
      depthOk,
      leftFilled: leftBuy.filled,
      rightFilled: rightSell.filled,
    };
  }, [coinAmount, buyOrderBook, sellOrderBook]);

  // ====== АНАЛИТИКА ВЫХОДА ======
  // const exitCalc = useMemo(() => {
  //   const requested = Number(coinAmount);
  //   if (!(requested > 0)) return null;

  //   const leftBids  = exitBuyOrderBook?.bids || buyOrderBook?.bids || [];
  //   const rightAsks = exitSellOrderBook?.asks || sellOrderBook?.asks || [];

  //   const leftSell = avgSellToBids(leftBids, requested);
  //   const rightBuy = avgBuyFromAsks(rightAsks, requested);

  //   const matchedVol = Math.min(leftSell.filled, rightBuy.filled);

  //   let useSell = leftSell;
  //   let useBuy  = rightBuy;
  //   let depthOk = leftSell.enoughDepth && rightBuy.enoughDepth;

  //   if (matchedVol > 0 && matchedVol < requested - 1e-12) {
  //     useSell = avgSellToBids(leftBids, matchedVol);
  //     useBuy  = avgBuyFromAsks(rightAsks, matchedVol);
  //   }

  //   const vol = matchedVol;
  //   const avgSellPrice = vol > 0 ? useSell.total / vol : 0;
  //   const avgBuyPrice  = vol > 0 ? useBuy.total  / vol : 0;
  //   const spreadPct = avgBuyPrice > 0 ? ((avgSellPrice - avgBuyPrice) / avgBuyPrice) * 100 : 0;

  //   return {
  //     requestedVolume: requested,
  //     matchedVolume: vol,
  //     avgSellPrice,
  //     avgBuyPrice,
  //     totalSellUSDT: useSell.total,
  //     totalBuyUSDT: useBuy.total,
  //     spreadPct,
  //     depthOk,
  //     leftFilled: leftSell.filled,
  //     rightFilled: rightBuy.filled,
  //   };
  // }, [coinAmount, buyOrderBook, sellOrderBook]);
  // Строки 196-234 заменить на:
const exitCalc = useMemo(() => {
  const requested = Number(coinAmount);
  if (!(requested > 0)) return null;

  // ✅ Используем переданные стаканы для выхода
  const leftBids  = exitBuyOrderBook?.bids || buyOrderBook?.bids || [];
  const rightAsks = exitSellOrderBook?.asks || sellOrderBook?.asks || [];

  const leftSell = avgSellToBids(leftBids, requested);
  const rightBuy = avgBuyFromAsks(rightAsks, requested);

  const matchedVol = Math.min(leftSell.filled, rightBuy.filled);

  let useSell = leftSell;
  let useBuy  = rightBuy;
  let depthOk = leftSell.enoughDepth && rightBuy.enoughDepth;

  if (matchedVol > 0 && matchedVol < requested - 1e-12) {
    useSell = avgSellToBids(leftBids, matchedVol);
    useBuy  = avgBuyFromAsks(rightAsks, matchedVol);
  }

  const vol = matchedVol;
  const avgSellPrice = vol > 0 ? useSell.total / vol : 0;
  const avgBuyPrice  = vol > 0 ? useBuy.total  / vol : 0;
  const spreadPct = avgBuyPrice > 0 ? ((avgSellPrice - avgBuyPrice) / avgBuyPrice) * 100 : 0;

  return {
    requestedVolume: requested,
    matchedVolume: vol,
    avgSellPrice,
    avgBuyPrice,
    totalSellUSDT: useSell.total,
    totalBuyUSDT: useBuy.total,
    spreadPct,
    depthOk,
    leftFilled: leftSell.filled,
    rightFilled: rightBuy.filled,
  };
}, [coinAmount, buyOrderBook, sellOrderBook, exitBuyOrderBook, exitSellOrderBook]); // ✅ Добавили зависимости

  // ====== «Доступный объём для входа (спред > 0)» ======
  const entryPositive = useMemo(() => {
    return maxPositiveEntryVolume(buyOrderBook?.asks, sellOrderBook?.bids);
  }, [buyOrderBook, sellOrderBook]);

  return (
    <div className="info-block dark">
      {/* ===== Ввод объёма ===== */}
      <div className="volume-input-row">
        <label>
          Желаемый объём:
          <input
            type="text"
            inputMode="decimal"
            placeholder="0.0"
            value={coinAmountStr}
            onChange={(e) => {
              const raw = e.target.value.replace(",", ".");
              setCoinAmountStr(raw);
              const v = parseFloat(raw);
              setCoinAmount(Number.isFinite(v) ? v : null);
            }}
            onBlur={() => {
              if (coinAmount == null || !(coinAmount >= 0)) {
                setCoinAmountStr("");
                return;
              }
              const rounded = roundN(coinAmount, 4);
              setCoinAmount(rounded);
              setCoinAmountStr(String(rounded));
            }}
          />
        </label>

        {/* Подсказка по покупке слева на введённый объём */}
        {Number(coinAmount) > 0 && (
          <div className="hint">
            {(() => {
              const vol = Number(coinAmount);
              const preview = avgBuyFromAsks(buyOrderBook?.asks || [], vol);
              const t = preview.total || 0;
              const f = preview.filled || 0;
              return `Стоимость: ${t.toFixed(2)} USDT${f < vol ? ' (ликвидности мало)' : ''}`;
            })()}
          </div>
        )}
      </div>

      <hr />

      <><div className="entry-positive">
        <div>Доступный объём для входа:</div>
        {entryPositive?.volume > 0 ? <><strong>{entryPositive.volume.toFixed(4)}</strong> монет</> : <strong>отсутствует</strong>}
      </div><hr /></>

      {/* ===== Аналитика ВХОДА (по введённому объёму) ===== */}
      {entryCalc ? (
        <>
          {/* <p>
            <strong>Вход (слева покупаем, справа продаём)</strong>
            {!entryCalc.depthOk && (
              <em className="warn">&nbsp;• Недостаточная ликвидность, считаем на совпадающем объёме</em>
            )}
          </p>
          <p>
            Объём учтён: {entryCalc.matchedVolume.toFixed(4)} / запрошено {entryCalc.requestedVolume.toFixed(4)}
          </p>
          <p>
            Ср. покупка слева: <strong>{entryCalc.avgBuyPrice.toFixed(8)}</strong> | Итого затрат: {entryCalc.totalBuyUSDT.toFixed(2)} USDT
          </p>
          <p>
            Ср. продажа справа: <strong>{entryCalc.avgSellPrice.toFixed(8)}</strong> | Итого выручка: {entryCalc.totalSellUSDT.toFixed(2)} USDT
          </p> */}
          <p>
            <strong>Спред входа:</strong> {Number.isFinite(entryCalc.spreadPct) ? entryCalc.spreadPct.toFixed(2) : "0.00"}%
          </p>
        </>
      ) : (
        <p>Нет данных</p>
      )}

      {/* <hr /> */}

      {/* ===== Аналитика ВЫХОДА (по введённому объёму) ===== */}
      {exitCalc ? (
        <>
          {/* <p>
            <strong>Выход (слева продаём, справа покупаем)</strong>
            {!exitCalc.depthOk && (
              <em className="warn">&nbsp;• Недостаточная ликвидность, считаем на совпадающем объёме</em>
            )}
          </p>
          <p>
            Объём учтён: {exitCalc.matchedVolume.toFixed(4)} / запрошено {exitCalc.requestedVolume.toFixed(4)}
          </p>
          <p>
            Ср. продажа слева: <strong>{exitCalc.avgSellPrice.toFixed(8)}</strong> | Итого выручка: {exitCalc.totalSellUSDT.toFixed(2)} USDT
          </p>
          <p>
            Ср. покупка справа: <strong>{exitCalc.avgBuyPrice.toFixed(8)}</strong> | Итого затрат: {exitCalc.totalBuyUSDT.toFixed(2)} USDT
          </p> */}
          <p>
            <strong>Спред выхода:</strong> {Number.isFinite(exitCalc.spreadPct) ? (exitCalc.spreadPct * (-1)).toFixed(2) : "0.00"}%
          </p>
        </>
      ) : (
        <p>Нет данных</p>
      )}

      {/* старые блоки оставлены закомментированными */}
    </div>
  );
};

export default InfoBlock;
